import React, { useState, useEffect } from 'react'
import shortid from 'shortid'

import Modal, { ModalHead, ModalBody, ModalFooter } from './modal'
import Heading from './heading'
import { clientInstance } from '../store/sagas'
import { formatForAPI } from '../helpers/dates'
import styles from '../component-styles/calendar-add-event.module.scss'
import Input, { Label } from './input'
import SelectBoxTypeahead from './select-box-typeahead'
import ConfirmationButtons from './confirmation-buttons'

const eventTypes = [
  'Agency',
  'Aggregator',
  'Business',
  'Campaign',
  'Competitor',
  'External',
  'Meeting',
  'Partner',
  'Personal',
  'Project',
  'PPC',
  'Offline',
  'SEO',
  'Store',
  'Technical',
  'Testing',
  'Tool',
  'World',
  'Other',
]

interface Event {
  eventTitle?: string
  eventData?: string
  eventType?: string
  eventID?: string
  endDate?: string
  startDate?: string
}

interface Props {
  active: boolean
  date: Date
  onSuccess: () => void
  toggleActive: () => void
  event?: Event | null
}

export async function onSubmit(
  formData: Event,
  date: Date,
  onSuccess: () => void,
  update = false,
): Promise<void> {
  const formDataWithDate = {
    ...formData,
    startDate: formatForAPI(date),
    endDate: formatForAPI(date),
  }

  try {
    let res
    if (update) {
      res = await clientInstance.updateEvent(formDataWithDate)
    } else {
      res = await clientInstance.createNewEvent(formDataWithDate)
    }
    if (res) {
      onSuccess()
    }
  } catch (e) {
    console.error(e)
  }
}

export async function onDelete(
  eventID: string,
  onSuccess: () => void,
): Promise<void> {
  if (eventID !== '') {
    try {
      const res = await clientInstance.deleteEvent({ eventID })

      if (res) {
        onSuccess()
      }
    } catch (e) {
      console.error(e)
    }
  }
}

export const initialState: Event = {
  eventTitle: '',
  eventData: '',
  eventType: '',
}

export default function AddEvent({
  active,
  toggleActive,
  onSuccess,
  date,
  event = null,
}: Props): React.ReactElement {
  const [formData, setFormData] = useState({
    ...(event !== null ? event : initialState),
  })
  const [key, setKey] = useState(shortid.generate())

  useEffect((): void => {
    if (event !== null) {
      setFormData({ ...event })
      setKey(shortid.generate())
    }
  }, [event])

  const updateForm = (type: string, next: string): void => {
    setFormData({
      ...formData,
      [type]: next,
    })
  }

  if (!active) {
    return <></>
  }

  return (
    <Modal key={key} active={active} toggleActive={toggleActive} narrow>
      <ModalHead>
        <Heading type={3} align="left">
          {event !== null ? 'Update or delete event' : 'Add a new event'}
        </Heading>
        <p className={styles.subTitle}>
          Add custom events to remind everyone of important factors which could
          impact performance and/or analytics.
        </p>
      </ModalHead>
      <form
        onSubmit={(e): void => {
          e.preventDefault()
          onSubmit(formData, date, onSuccess, event !== null)
          toggleActive()
        }}
      >
        <ModalBody>
          <Label modalHeading id="title">
            Event title
          </Label>
          <Input
            required
            name="title"
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
              const { value: val } = e.target as HTMLInputElement
              updateForm('eventTitle', val)
            }}
            value={formData.eventTitle}
            placeholder="What is the name of the event?"
          />
          <Label modalHeading id="event-type">
            Event type
          </Label>
          <SelectBoxTypeahead
            id="event-type"
            placeholder="What type of event?"
            value={formData.eventType}
            onInputChange={(val: string): void => {
              updateForm('eventType', val)
            }}
            items={eventTypes}
          />
          <Label modalHeading id="message">
            Event message
          </Label>
          <Input
            required
            name="message"
            onChange={(val: string): void => {
              updateForm('eventData', val)
            }}
            value={formData.eventData}
            type="textArea"
            placeholder="What happened?"
          />
        </ModalBody>
        <ModalFooter>
          <ConfirmationButtons
            type="simple"
            onYes={(): void => {
              if (event !== null) {
                onDelete(event.eventID || '', onSuccess)
                toggleActive()
              }
            }}
            onNo={(): void => {
              toggleActive()
            }}
            yesLabel={event !== null ? 'Delete' : 'Add event'}
            noLabel="Go back"
            secondaryYesLabel={event !== null ? 'Update' : null}
          />
        </ModalFooter>
      </form>
    </Modal>
  )
}
